import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { track } from "@/lib/analytics";
import { recordListening } from "@/lib/account.functions";
import { supabase } from "@/integrations/supabase/client";

export interface PlayerTrack {
  /** Stable id — e.g. `${releaseSlug}` or `${releaseSlug}:${songSlug}` */
  id: string;
  title: string;
  subtitle?: string;         // e.g. release title, or "Buddha KA"
  audioUrl: string;
  coverUrl?: string;
  href?: string;             // link to the detail page
}

interface PlayerState {
  queue: PlayerTrack[];
  index: number;             // current index in queue; -1 when empty
  isPlaying: boolean;
  currentTime: number;
  duration: number;
}

interface PlayerContextValue extends PlayerState {
  current: PlayerTrack | null;
  playTrack: (track: PlayerTrack, queue?: PlayerTrack[]) => void;
  toggle: () => void;
  play: () => void;
  pause: () => void;
  next: () => void;
  previous: () => void;
  seek: (seconds: number) => void;
  clear: () => void;
}

const Ctx = createContext<PlayerContextValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PlayerState>({
    queue: [],
    index: -1,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
  });

  // Ensure audio element exists client-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "metadata";
      audioRef.current = el;
    }
    const el = audioRef.current;
    const onTime = () => setState((s) => ({ ...s, currentTime: el.currentTime, duration: el.duration || 0 }));
    const onPlay = () => setState((s) => ({ ...s, isPlaying: true }));
    const onPause = () => setState((s) => ({ ...s, isPlaying: false }));
    const onEnded = () => {
      setState((s) => {
        const nextIndex = s.index + 1;
        if (nextIndex < s.queue.length) return { ...s, index: nextIndex, currentTime: 0 };
        return { ...s, isPlaying: false, currentTime: 0 };
      });
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, []);

  const current = state.index >= 0 ? state.queue[state.index] ?? null : null;

  // Load new source when the current track changes.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (el.src !== current.audioUrl) {
      el.src = current.audioUrl;
      // autoplay on selection
      void el.play().catch(() => {});
      track("player_play", { id: current.id, title: current.title });
      // Record listening history for signed-in users. id format: "release" or "release:song"
      void (async () => {
        try {
          const { data } = await supabase.auth.getUser();
          if (!data.user) return;
          const [release_slug, song_slug] = current.id.split(":");
          if (!release_slug) return;
          await recordListening({ data: { release_slug, song_slug: song_slug ?? null } });
        } catch {
          /* noop */
        }
      })();
    }
  }, [current?.id, current?.audioUrl]);

  const playTrack = useCallback((t: PlayerTrack, queue?: PlayerTrack[]) => {
    setState((s) => {
      const q = queue && queue.length ? queue : [t];
      const idx = Math.max(0, q.findIndex((x) => x.id === t.id));
      return { ...s, queue: q, index: idx };
    });
  }, []);

  const play = useCallback(() => {
    audioRef.current?.play().catch(() => {});
  }, []);
  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);
  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => {});
    else el.pause();
  }, []);
  const next = useCallback(() => {
    setState((s) => (s.index + 1 < s.queue.length ? { ...s, index: s.index + 1, currentTime: 0 } : s));
  }, []);
  const previous = useCallback(() => {
    setState((s) => (s.index > 0 ? { ...s, index: s.index - 1, currentTime: 0 } : s));
  }, []);
  const seek = useCallback((seconds: number) => {
    if (audioRef.current) audioRef.current.currentTime = seconds;
  }, []);
  const clear = useCallback(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.src = "";
    setState({ queue: [], index: -1, isPlaying: false, currentTime: 0, duration: 0 });
  }, []);

  const value = useMemo<PlayerContextValue>(
    () => ({ ...state, current, playTrack, toggle, play, pause, next, previous, seek, clear }),
    [state, current, playTrack, toggle, play, pause, next, previous, seek, clear],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlayer(): PlayerContextValue {
  const v = useContext(Ctx);
  if (!v) {
    // Safe no-op during SSR or when provider missing.
    return {
      queue: [], index: -1, isPlaying: false, currentTime: 0, duration: 0, current: null,
      playTrack: () => {}, toggle: () => {}, play: () => {}, pause: () => {},
      next: () => {}, previous: () => {}, seek: () => {}, clear: () => {},
    };
  }
  return v;
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
